import {ProductInstance, RouterEntity, WifiEntity, WifiMetadata, WlanEntity} from "../../../util/types.ts";
import {useCallback, useEffect, useState} from "react";
import {getWifiStatus, updateWifi} from "../../../util/request.ts";
import LoaderSpinner from "../../loaders/LoaderSpinner.tsx";
import {Link} from "react-router-dom";
import Icon from "../Icon.tsx";
import IconButton from "../IconButton.tsx";
import Cookies from "js-cookie";
import {isValidSSID, isValidWifiPassword} from "../../../util/validators.tsx";
import {formatDateTime} from "../../../util/formatter.ts";
import IconCopy from "../../icons/IconCopy.tsx";
import IconInfo from "../../icons/IconInfo.tsx";
import IconEyeClosed from "../../icons/IconEyeClosed.tsx";
import IconEye from "../../icons/IconEye.tsx";

export default function Wifi({customer_id, product_instance}: { customer_id: string; product_instance: ProductInstance; }) {
    const [wifi, setWifi] = useState<WifiMetadata | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');

    const getWifi = useCallback(() => {
        setLoading(true);
        getWifiStatus(customer_id, product_instance.parent_product_instance_id)
            .then((r) => {
                setWifi(r.response.data);
                if (['FAIL', 'NO DATA'].includes(r.response.data.result)) {
                    setError('Vi kan ikke hente data for dit signalstyrke lige nu. Prøv igen senere.')
                }
            })
            .finally(() => setLoading(false));
    }, [customer_id, product_instance.parent_product_instance_id]);

    useEffect(() => {
        getWifi()
    }, [getWifi]);

    if (loading && !wifi) {
        return <LoaderSpinner/>;
    }

    if (error) {
        return <p className={'error-message'}>{error}</p>;
    }

    if (!wifi) {
        return <p className={'error-message'}>Noget gik galt. Genindlæs siden eller prøv igen senere.</p>;
    }

    return <>
        {Object.values(wifi.wlans).length > 0 && (
            <WifiSettings customer_id={customer_id} product_instance={product_instance} wlans={wifi.wlans} router={wifi.router}/>
        )}
        <WifiEntities wifi={wifi} getWifi={getWifi} loading={loading}/>
    </>
}

function WifiSettings({customer_id, product_instance, wlans, router}: {
    customer_id: string;
    product_instance: ProductInstance;
    wlans: Record<string, WlanEntity>;
    router: RouterEntity;
}) {
    const TEMP_WIFI_CONFIG = 'tempWifiConfig';
    const WIFI2GHZ = 'id1';
    const WIFI5GHZ = 'id5';

    const temp_wifi_config = Cookies.get(TEMP_WIFI_CONFIG);
    const parsedWifiConfig = temp_wifi_config ? JSON.parse(temp_wifi_config) : {};

    const wlanList = Object.values(wlans);
    const wifi2ghz = wlanList.find(w => w.ifid === WIFI2GHZ) || wlanList[0];
    const wifi5ghz = wlanList.find(w => w.ifid === WIFI5GHZ) || wlanList[1];

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<boolean>(
        parsedWifiConfig.wifiName2ghz !== undefined ||
        parsedWifiConfig.wifiName5ghz !== undefined ||
        parsedWifiConfig.wifiSplit !== undefined
    );
    const [wifiName2ghz, setWifiName2ghz] = useState<string>(parsedWifiConfig.wifiName2ghz ?? wifi2ghz.ssid);
    const [wifiPassword2ghz, setWifiPassword2ghz] = useState<string>('');
    const [wifiName5ghz, setWifiName5ghz] = useState<string>(parsedWifiConfig.wifiName5ghz ?? wifi5ghz.ssid);
    const [wifiPassword5ghz, setWifiPassword5ghz] = useState<string>('');
    const [wifiSplit, setWifiSplit] = useState<boolean>(parsedWifiConfig.wifiSplit ? parsedWifiConfig.wifiSplit === '1' : wifi2ghz?.mpro === 'false');
    const [showEditCredentialsForm, setShowEditCredentialsForm] = useState(false);

    const [ssidError, setSsidError] = useState<boolean>(false);
    const [passwordError, setPasswordError] = useState<boolean>(false);
    const [ssid5ghzError, setSsid5ghzError] = useState<boolean>(false);
    const [password5ghzError, setPassword5ghzError] = useState<boolean>(false);

    const [passwordPreview, setPasswordPreview] = useState<boolean>(false);

    const handleWifiCredentialsChange = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setSuccess(false)

        const validSsid = isValidSSID(wifiName2ghz);
        const validPassword = isValidWifiPassword(wifiPassword2ghz);
        setSsidError(!validSsid);
        setPasswordError(!validPassword);

        let validSsid5:boolean = false;
        let validPassword5:boolean = false;

        if (wifiSplit) {
            validSsid5 = isValidSSID(wifiName5ghz);
            validPassword5 = isValidWifiPassword(wifiPassword5ghz);
            setSsid5ghzError(!validSsid5);
            setPassword5ghzError(!validPassword5);
        }

        const hasError =
            !validSsid || !validPassword || !validSsid5 || !validPassword5;

        if (hasError) {
            return;
        }

        setLoading(true)
        updateWifi(customer_id, product_instance.parent_product_instance_id, {
            'serial': router?.serial,
            'wifiName2ghz': wifiName2ghz,
            'wifiPassword2ghz': wifiPassword2ghz,
            'wifiName5ghz': wifiSplit ? wifiName5ghz : '',
            'wifiPassword5ghz': wifiSplit ? wifiPassword5ghz : '',
            'oneSsid': !wifiSplit,
        })
            .then((r) => {
                if (r.code === 200) {
                    setSuccess(true)

                    const tenMinutes = 1 / 144;
                    Cookies.set(TEMP_WIFI_CONFIG, JSON.stringify({
                        wifiName2ghz,
                        wifiName5ghz,
                        wifiSplit: wifiSplit ? "1" : "0",
                    }), { expires: tenMinutes });
                } else {
                    setError('Noget gik galt. Genindlæs siden eller prøv igen senere.');
                }
                setShowEditCredentialsForm(false);
            })
            .catch(() => {
                setError('Noget gik galt. Genindlæs siden eller prøv igen senere.');
            })
            .finally(() => {
                    setShowEditCredentialsForm(false);
                    setLoading(false)
                }
            )
    };

    const copyToClipboard = useCallback((text: string) => {
        navigator.clipboard.writeText(text)
            .then(() => alert(`${text} er kopieret.`));
    }, []);

    const wlanDisplay = (wlan: WlanEntity, ssid: string) => (
        <div className={'wifi-login'}>
            {wifiSplit && (
                <h3 className={'heading heading--montserrat heading--small'}>
                    {wlan.band}
                </h3>
            )}
            <p>
                <span>Wifi navn:</span>
                <button
                    className={'button button--montserrat gray with-icon'}
                    onClick={() => copyToClipboard(ssid)}
                >
                    <span className={'text'}>
                        {ssid}
                    </span>
                    <Icon component={IconCopy}/>
                </button>
            </p>
            <p>
                <span>Wifi kode:</span>
                <span className={'password'}>&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;&nbsp;</span>
                <span className={'tooltip'}>
                    <Icon component={IconInfo}/>

                    <span className={'tooltip-text'}>
                        Vi har som standard ikke adgang til din wifi-kode og kan derfor ikke vise den.
                        Du kan altid ændre den ved at vælge &ldquo;Redigér&rdquo;.
                    </span>
                </span>
            </p>
        </div>
    );

    const wlanForm = (
        wifi: WlanEntity,
        ssid: string,
        setSsid: React.Dispatch<React.SetStateAction<string>>,
        password: string,
        setPassword: React.Dispatch<React.SetStateAction<string>>
    ) => (
        <div className={'wifi-login-edit'}>
            {wifiSplit && (
                <h3 className={'heading heading--montserrat heading--small'}>
                    {wifi.band}
                </h3>
            )}
            <div className={'input-container'}>
                <p>Wifi navn</p>
                <input
                    type={'text'}
                    placeholder={'Indtast nyt navn'}
                    value={ssid}
                    onChange={(e) => setSsid(e.target.value)}
                    required
                />
                {wifi.ifid === WIFI2GHZ && ssidError && (
                    <SSIDErrorMessage/>
                )}
                {wifi.ifid === WIFI5GHZ && ssid5ghzError && (
                    <SSIDErrorMessage/>
                )}
            </div>
            <div className={'input-container'}>
                <p>Wifi kode</p>
                <p className={'input-with-button'}>
                    <input
                        type={passwordPreview ? 'text' : 'password'}
                        placeholder={'Indtast ny kode'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    <span className="button-container">
                    <IconButton
                        className='icon-button'
                        component={passwordPreview ? IconEyeClosed : IconEye}
                        onClick={() => setPasswordPreview(!passwordPreview)}
                    />
                </span>
                </p>
                {wifi.ifid === WIFI2GHZ && passwordError && (
                    <WifiPasswordErrorMessage />
                )}
                {wifi.ifid === WIFI5GHZ && password5ghzError && (
                    <WifiPasswordErrorMessage />
                )}
            </div>
        </div>
    );

    return (
        <div className={'selfcare-wifi-settings'}>
            <WifiSettingsHeader/>

            {!showEditCredentialsForm && (
                <>
                    {success && (
                        <p className={'success-message'}>
                            Sådan! Det kan tage et par minutter, før din ændring træder i kraft.
                            Husk at logge alle dine enheder på dit wifi igen.
                        </p>
                    )}
                    {error && (
                        <p className={'error-message'}>
                            {error}
                        </p>
                    )}
                    <div className={'wifi-settings-container'}>
                        <div className={`wifi-login-container ${wifiSplit ? 'multiple' : 'single'}`}>
                            {wlanDisplay(wifi2ghz, wifiName2ghz)}
                            {wifiSplit && (
                                <>
                                    <span className={'vertical-line'}/>
                                    {wlanDisplay(wifi5ghz, wifiName5ghz)}
                                </>
                            )}
                        </div>
                        <div className={'buttons'}>
                            <button
                                onClick={() => setShowEditCredentialsForm(true)}
                                className={'button button--montserrat teal rounded'}
                            >
                                Redigér
                            </button>
                        </div>
                    </div>
                </>
            )}

            {showEditCredentialsForm && (
                <form className={'wifi-settings-container'} onSubmit={handleWifiCredentialsChange}>
                    {loading && <LoaderSpinner/>}
                    <p className={'with-checkbox split-wifi'}>
                        <input
                            type={'checkbox'}
                            name={'split_wifi'}
                            id={'split_wifi'}
                            checked={wifiSplit}
                            onChange={() => setWifiSplit(!wifiSplit)}
                        />
                        <label
                            htmlFor={'split_wifi'}
                            className={'checkbox-label checkbox-label--montserrat'}
                        >
                            Giv 2,4- og 5 GHz-netværkene to forskellige navne
                            <Link className={'icon-link'} to={'/hjaelp/internet-og-wifi/forskel-paa-signal-frekvenser'} target={'_blank'}>
                                <Icon component={IconInfo}/>
                            </Link>
                        </label>
                    </p>

                    <div className={`wifi-login-edit-container ${wifiSplit ? 'multiple' : 'single'}`}>
                        {wlanForm(wifi2ghz, wifiName2ghz, setWifiName2ghz, wifiPassword2ghz, setWifiPassword2ghz)}
                        {wifiSplit && (
                            <>
                                <span className={'vertical-line'}/>
                                {wlanForm(wifi5ghz, wifiName5ghz, setWifiName5ghz, wifiPassword5ghz, setWifiPassword5ghz)}
                            </>
                        )}
                    </div>

                    <div className={'buttons'}>
                        <button
                            type={'button'}
                            className={'button button--montserrat gray rounded'}
                            onClick={() => setShowEditCredentialsForm(false)}
                        >
                            Annullér
                        </button>
                        <button
                            type={'submit'}
                            className={'button button--montserrat teal rounded'}
                        >
                            Bekræft
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}

function WifiEntities({wifi, getWifi, loading}: {
    wifi: WifiMetadata;
    getWifi: () => void;
    loading: boolean
}) {
    const [showAll, setShowAll] = useState<boolean>(false);
    const [toggleAdvanced, setToggleAdvanced] = useState<boolean>(false);
    const entitiesSorted = Object.entries(wifi.macs).sort(([, a], [, b]) => Number(b.ago) - Number(a.ago));
    const entitiesToShow = showAll ? entitiesSorted : entitiesSorted.slice(0, 4);

    return (
        <div className={'selfcare-wifi-device-information'}>
            <WifiEntitiesHeader/>

            <div className={'info-box'}>
                <Icon component={IconInfo}/>
                <p>
                    Oversigten nedenfor viser de enheder, der har været på dit wifi de seneste 24 timer.
                    Data bliver opdateret i vores system ca. hvert 5. minut.
                    Vælg &ldquo;Opdatér&rdquo; for at tjekke, om der er opdateret data tilgængelig.
                </p>
            </div>

            <p className={'advanced-data-toggle-container'}>
                Nørdedata
                <input
                    type={'checkbox'}
                    name={'toggle_advanced_data'}
                    id={'toggle_advanced_data'}
                    checked={toggleAdvanced}
                    onChange={() => setToggleAdvanced(!toggleAdvanced)}
                />
                <label
                    htmlFor={'toggle_advanced_data'}
                    className={`advanced-data-toggle${toggleAdvanced ? ' active' : ''}`}
                />
                {toggleAdvanced && (
                    <span className={'tooltip'}>
                        <Icon component={IconInfo}/>
                        <span className={'tooltip-text'}>
                            <strong>Hvad er dBM og hvorfor er den negativ?</strong>
                            <br/>
                            dBm (decibel-milliwatt) er en enhed, der bruges til at måle signalstyrke i f.eks. trådløse wifi-netværk.
                            <br/>
                            dBm angiver effekten af dit signal i forhold til 1 milliwatt.
                            Derfor er den altid negativ, når vi snakker wifi-signalstyrke, og jo tættere på 0, des stærkere wifi-signal har enheden.
                            <br/>
                            For eksempel er -50 dBm stærkere end -70 dBm.
                            <br/><br/>
                            <strong>-50 dBm til -60 dBm (eller bedre)</strong>
                            <br/>
                            Din enhed har et meget stærkt og pålideligt wifi-signal. Det er ideelt til aktiviteter med højt båndbreddeforbrug.
                            <br/><br/>
                            <strong>-61 dBm til -70 dBm</strong>
                            <br/>
                            Enheden har en god og brugbar wifi-forbindelse til de fleste almindelige onlineopgaver, selvom ydeevnen kan falde en smule.
                            <br/><br/>
                            <strong>-71 dBm og derunder</strong>
                            <br/>
                            Din enheds signalstyrke er svagt eller meget svagt. Stabilitet og hastighed på forbindelsen kan blive væsentligt påvirket.
                        </span>
                    </span>
                )}
            </p>

            <div className={'device-overview-container'}>
                <div className={'device-overview'}>
                    {entitiesToShow
                        .map(([mac, entity]) => (
                            <EntityWifiStatus key={mac} entity={entity} toggleAdvanced={toggleAdvanced}/>
                        ))
                    }
                    {entitiesSorted.length === 0 && (
                        <>Vi kan ikke se nogle enheder på dit wifi.</>
                    )}
                </div>
                <div className={'buttons'}>
                    {loading ? <LoaderSpinner/> :
                        <>
                            <button className={'button button--montserrat teal rounded'} disabled={loading} onClick={getWifi}>Opdatér</button>
                            {!showAll && entitiesSorted.length > 4 ? (
                                <button
                                    className={'button button--montserrat teal rounded'}
                                    onClick={() => setShowAll(!showAll)}
                                >Vis alle enheder</button>
                            ) : (
                                entitiesSorted.length > 0 && (
                                    <button
                                        className={'button button--montserrat teal rounded'}
                                        onClick={() => setShowAll(!showAll)}
                                    >Vis færre enheder</button>
                                )
                            )}
                        </>
                    }
                </div>
            </div>
        </div>
    )
}

function EntityWifiStatus({entity, toggleAdvanced}: {
    entity: WifiEntity;
    toggleAdvanced: boolean,
}) {
    return (
        <div className={'device-container'}>
            <div className={`device${toggleAdvanced ? ' advanced' : ''}`}>
                {toggleAdvanced ? (
                    <p>
                        <strong>Enhed:</strong> {entity.name}<br/>
                        <strong>Frekvens:</strong> {entity.band}&nbsp;GHz<br/>
                        <strong>Wifi-signalstyrke:</strong> {entity.rssi} dBm<br/>
                        <strong>Sidst set online:</strong> {formatDateTime(entity.last)}
                    </p>
                ) : (
                    <p>
                        {entity.name}<br/>
                        <span className={'small'}>{entity.band}&nbsp;GHz</span>
                    </p>
                )}
                <div className={'signal-bars-container'}>
                    <span className={`signal-bars signal-bars--${getWifiStatusColor(entity.rssi).className}`}/>
                </div>
            </div>
            {entity.rssi <= -71 &&
                <div className={'message'}>
                    <p>
                        Wifi-signalet til enheden er svagt.
                        Prøv at flytte enheden tættere på routeren
                        {entity.band === '5' && (<>
                            {' '}eller <Link
                            to={'/hjaelp/internet-og-wifi/forskel-paa-signal-frekvenser'}
                            target={'_blank'}
                        >forbind til 2,4&nbsp;GHz wifi</Link>
                        </>)}.
                    </p>
                </div>
            }
        </div>
    );
}

function getWifiStatusColor(rssi: number): { signal: string; translated: string; className: string } {
    if (rssi >= -50) {
        return {signal: 'Excellent', translated: 'Rigtig god', className: 'excellent'}
    }
    if (rssi >= -60) {
        return {signal: 'Good', translated: 'Rigtig god', className: 'excellent'}
    }
    if (rssi >= -70) {
        return {signal: 'Fair', translated: 'God', className: 'good'}
    }
    if (rssi >= -80) {
        return {signal: 'Weak', translated: 'Svag', className: 'poor'}
    }
    if (rssi >= -90) {
        return {signal: 'Very weak', translated: 'Svag', className: 'poor'}
    }

    return {signal: 'No signal', translated: 'Intet signal', className: 'no-signal'}
}

export function WifiHeader() {
    return (
        <div className={'title'}>
            <h2 className={'heading heading--montserrat'}>Wifi indstillinger og oversigt</h2>
            <p>Her kan du ændre navn og adgangskode, samt se enheder på dit wifi.</p>
        </div>
    )
}

export function WifiEntitiesHeader() {
    return (
        <div className={'title'}>
            <h2 className={'heading heading--montserrat'}>Signalstyrke på dine enheder</h2>
            <p>
                Her kan du se enheder på dit wifi.
            </p>
            <p>
                <span className={'hide-on-large-devices'}>Tryk på{' '}</span>
                <span className={'hide-on-small-devices'}>Hold musen over{' '}</span>
                en enhed for at se flere informationer.
            </p>
        </div>
    )
}

export function WifiSettingsHeader() {
    return (
        <div className={'title'}>
            <h2 className={'heading heading--montserrat'}>Dit wifi-navn og -kode</h2>
            <p>
                Her kan du se dit wifi-navn og ændre dit wifi-navn og -kode.
            </p>
            <p>
                Hvis du ændrer navn eller kode, skal du logge alle dine enheder på dit wifi igen.
            </p>
        </div>
    )
}

 function SSIDErrorMessage() {
    return (
        <div className={'error-message'}>
            <p>
                Skal opfylde følgende krav:
            </p>
            <ul>
                <li>
                    Kun bogstaver, tal, bindestreg og bundstreg er tilladt.
                </li>
                <li>
                    Må ikke indeholde æ, ø eller å.
                </li>
                <li>
                    Maks. 32 tegn.
                </li>
            </ul>
        </div>
    )
}

 function WifiPasswordErrorMessage() {
    return (
        <div className={'error-message'}>
            <p>
                Skal opfylde følgende krav:
            </p>
            <ul>
                <li>
                    Skal indeholde mindst ét stort bogstav, ét lille bogstav, ét tal og ét specialtegn.
                </li>
                <li>
                    Mellem 8-64 tegn.
                </li>
            </ul>
        </div>
    )
}
